/**
 * Os horários do cliente.
 *
 * A tela abre no próximo horário, que é a única informação que ele veio buscar
 * em 95% das vezes: "que dia mesmo eu marquei?".
 */

import type { AppointmentView } from '@barbearia/shared';
import { describeServices, STATUS_LABELS } from '@barbearia/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ScreenHeader } from '../../App.tsx';
import { Badge, Button, EmptyState, Spinner } from '../../components/ui.tsx';
import { ApiError, api } from '../../lib/api.ts';
import { useTenant } from '../../lib/contexts.tsx';
import { duration, longDateOf, money, timeOf } from '../../lib/format.ts';
import { MyWaitlist } from './MyWaitlist.tsx';

interface MyAppointmentsResponse {
  upcoming: AppointmentView[];
  history: AppointmentView[];
}

export function MyAppointments() {
  const { tenant } = useTenant();
  const queryClient = useQueryClient();

  const { data, isPending } = useQuery({
    queryKey: ['my-appointments'],
    queryFn: () => api.get<MyAppointmentsResponse>('/appointments/mine'),
  });

  const cancel = useMutation({
    mutationFn: (id: string) =>
      api.post(`/appointments/${id}/status`, { status: 'CANCELED_BY_CLIENT' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['my-appointments'] }),
  });

  const next = data?.upcoming[0];

  return (
    <div className="flex min-h-full flex-col pb-10">
      <ScreenHeader title="Meus horários" />

      {isPending && <Spinner />}

      {data && data.upcoming.length === 0 && (
        <EmptyState
          title="Nenhum horário marcado"
          description={`Escolha o serviço e o horário na ${tenant.name}.`}
          action={
            <Link to="/agendar">
              <Button>Agendar</Button>
            </Link>
          }
        />
      )}

      {next && (
        <section className="px-4 pt-4">
          <div className="rounded-2xl border border-primary bg-primary/10 p-5">
            <p className="text-[13px] font-semibold tracking-wide text-ink-soft uppercase">
              Seu próximo horário
            </p>
            <p className="pt-2 text-[28px] leading-none font-bold tabular-nums text-ink">
              {timeOf(next.startsAt, tenant.timezone)}
            </p>
            <p className="pt-1 text-[15px] text-ink">
              {longDateOf(next.startsAt.slice(0, 10), tenant.timezone)}
            </p>
            <p className="pt-3 text-[14px] text-ink-soft">
              {describeServices(next.services.map((service) => service.name))} ·{' '}
              {duration(next.durationMinutes)} · com {next.staff.name}
            </p>

            <div className="flex flex-wrap gap-2 pt-3">
              <Badge tone={next.status === 'REQUESTED' ? 'warn' : 'ok'}>
                {STATUS_LABELS[next.status]}
              </Badge>
              {/* Explica a terça que ele não pediu esta semana. */}
              {next.series && <Badge>↻ {next.series.label}</Badge>}
            </div>

            {next.status === 'REQUESTED' && (
              <p className="pt-3 text-[13px] text-ink-soft">
                A barbearia ainda vai confirmar. Você recebe o aviso aqui mesmo.
              </p>
            )}

            {/*
              A barbearia mexeu no que foi pedido. Este aviso é o mais
              importante da tela: aceitar sem perceber que o horário mudou é a
              forma mais fácil de alguém perder o corte.
            */}
            {next.adjusted && next.requested && (
              <div className="mt-4 rounded-xl border-2 border-warn bg-warn/15 p-4">
                <p className="text-[14px] font-bold text-ink">⚠ A barbearia ajustou seu horário</p>
                <div className="pt-2 text-[14px] text-ink">
                  {next.adjusted.time && (
                    <p>
                      Você pediu{' '}
                      <span className="font-medium line-through opacity-70">
                        {timeOf(next.requested.startsAt, tenant.timezone)}
                      </span>{' '}
                      → ficou{' '}
                      <span className="font-bold">{timeOf(next.startsAt, tenant.timezone)}</span>
                    </p>
                  )}
                  {next.adjusted.duration && (
                    <p>
                      Tempo:{' '}
                      <span className="line-through opacity-70">
                        {duration(next.requested.durationMinutes)}
                      </span>{' '}
                      → <span className="font-bold">{duration(next.durationMinutes)}</span>
                    </p>
                  )}
                  {/* O serviço que saiu do pedido é a mudança mais fácil de
                      não perceber: a pessoa chega esperando fazer a barba e o
                      barbeiro já sabia que não ia dar. */}
                  {next.adjusted.services && (
                    <p>
                      Você pediu{' '}
                      <span className="font-medium line-through opacity-70">
                        {describeServices(next.requested.services.map((service) => service.name))}
                      </span>{' '}
                      → ficou{' '}
                      <span className="font-bold">
                        {describeServices(next.services.map((service) => service.name))}
                      </span>
                    </p>
                  )}
                </div>
              </div>
            )}

            {next.staffNote && (
              <div className="mt-3 rounded-xl border border-line bg-surface px-4 py-3">
                <p className="text-[12px] font-semibold text-ink-faint">Recado da barbearia</p>
                <p className="pt-1 text-[14px] text-ink">{next.staffNote}</p>
              </div>
            )}

            <div className="flex flex-col gap-2 pt-4">
              {tenant.phone && (
                <a
                  href={`https://wa.me/${tenant.phone.replace(/\D+/g, '')}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Button variant="secondary" full>
                    Falar com a barbearia
                  </Button>
                </a>
              )}
              <Button
                variant="ghost"
                full
                loading={cancel.isPending}
                onClick={() => cancel.mutate(next.id)}
              >
                Cancelar horário
              </Button>
              {cancel.error instanceof ApiError && (
                <p className="text-center text-[13px] text-danger">{cancel.error.message}</p>
              )}
            </div>
          </div>
        </section>
      )}

      {data && data.upcoming.length > 1 && (
        <section className="px-4 pt-6">
          <h2 className="pb-2 text-[13px] font-semibold tracking-wide text-ink-faint uppercase">
            Também marcados
          </h2>
          <ul className="overflow-hidden rounded-xl border border-line">
            {data.upcoming.slice(1).map((appointment) => (
              <li
                key={appointment.id}
                className="flex items-center justify-between gap-3 border-b border-line bg-surface-raised px-4 py-3 last:border-b-0"
              >
                <span>
                  <span className="block text-[15px] font-medium text-ink">
                    {longDateOf(appointment.startsAt.slice(0, 10), tenant.timezone)}
                  </span>
                  <span className="block text-[13px] text-ink-soft">
                    {describeServices(appointment.services.map((service) => service.name))}
                    {appointment.series && ' · horário fixo'}
                  </span>
                </span>
                <span className="text-[16px] font-semibold tabular-nums text-ink">
                  {timeOf(appointment.startsAt, tenant.timezone)}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {data && data.history.length > 0 && (
        <section className="px-4 pt-6">
          <h2 className="pb-2 text-[13px] font-semibold tracking-wide text-ink-faint uppercase">
            Já passou
          </h2>
          <ul className="overflow-hidden rounded-xl border border-line">
            {data.history.slice(0, 10).map((appointment) => (
              <li
                key={appointment.id}
                className="border-b border-line bg-surface-raised px-4 py-3 last:border-b-0"
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="min-w-0">
                    <span className="block truncate text-[14px] text-ink">
                      {describeServices(appointment.services.map((service) => service.name))}
                    </span>
                    <span className="block text-[12px] text-ink-faint">
                      {longDateOf(appointment.startsAt.slice(0, 10), tenant.timezone)}
                    </span>
                  </span>
                  <span className="shrink-0 text-right">
                    <span className="block text-[13px] text-ink-soft">
                      {STATUS_LABELS[appointment.status]}
                    </span>
                    {appointment.priceCents != null && (
                      <span className="block text-[13px] tabular-nums text-ink-faint">
                        {money(appointment.priceCents)}
                      </span>
                    )}
                  </span>
                </div>

                {/* Um pedido recusado sem explicação parece descaso. Quando o
                    barbeiro escreveu o motivo, ele aparece junto. */}
                {appointment.staffNote && (
                  <p className="pt-2 text-[13px] text-ink-soft italic">“{appointment.staffNote}”</p>
                )}

                {/* Só chegam aqui as fotos que o barbeiro liberou — a API
                    filtra por quem está lendo, e a tela não tem como mostrar
                    uma que não deveria. */}
                {appointment.photos.length > 0 && (
                  <div className="scroll-touch flex gap-2 overflow-x-auto pt-3">
                    {appointment.photos.map((photo) => (
                      <img
                        key={photo.id}
                        src={photo.url}
                        alt=""
                        loading="lazy"
                        className="h-20 w-20 shrink-0 rounded-lg object-cover"
                      />
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      <MyWaitlist />

      <div className="px-4 pt-6">
        <Link to="/agendar">
          <Button full>Marcar outro horário</Button>
        </Link>
      </div>
    </div>
  );
}
